import { useEffect, useState } from 'react'

interface CountryData {
  name: string
  code: string
  emoji: string
}

let countriesCache: Map<string, CountryData> | null = null
let loadPromise: Promise<void> | null = null

function convertISOCodeToEmoji(isoCode: string): string {
  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

async function loadCountriesFromCSV(): Promise<void> {
  if (countriesCache) return
  
  try {
    const response = await fetch('/assets/countries-with-iso-codes.csv')
    const text = await response.text()
    const lines = text.split('\n').slice(1) // Skip header
    
    const countries = new Map<string, CountryData>()
    
    lines.forEach(line => {
      if (!line.trim()) return
      
      const parts = line.split(',')
      if (parts.length < 3) return // Now expecting 3 columns: country,iso2,iso3
      
      // Handle quoted country names with commas
      let name = parts[0]
      let iso2Code = parts[1]
      
      // If name starts with quote, handle special case
      if (name.startsWith('"')) {
        const endQuoteIndex = line.indexOf('"', 1)
        if (endQuoteIndex !== -1) {
          name = line.substring(1, endQuoteIndex)
          const afterName = line.substring(endQuoteIndex + 1)
          const remainingParts = afterName.split(',')
          iso2Code = remainingParts[1] // Skip the first comma after quote, take iso2
        }
      }
      
      name = name.trim()
      iso2Code = iso2Code.trim().toLowerCase()
      
      if (name && iso2Code) {
        const emoji = convertISOCodeToEmoji(iso2Code)
        countries.set(name, { name, code: iso2Code, emoji })
      }
    })
    
    countriesCache = countries
  } catch (error) {
    console.error('[CountryFlags] Error loading countries CSV:', error)
    countriesCache = new Map()
  }
}

function ensureCountriesLoaded(): Promise<void> {
  if (!loadPromise) {
    loadPromise = loadCountriesFromCSV()
  }
  return loadPromise
}

function getCountryData(country: string | undefined): CountryData | null {
  if (!country || !countriesCache) return null
  return countriesCache.get(country) || null
}

export function getCountryCode(country: string | undefined): string | null {
  const data = getCountryData(country)
  return data?.code || null
}

export function getCountryFlagEmoji(country: string | undefined): string {
  if (!country) return '🌍'
  const data = getCountryData(country)
  return data?.emoji || '🌍'
}

export function getCountryFlagHtml(country: string | undefined): string {
  const code = getCountryCode(country)
  if (!code) return '🌍'
  return `<span class="fi fi-${code} fis"></span>`
}

interface CountryFlagProps {
  country: string | undefined
  className?: string
  size?: 'sm' | 'md' | 'lg'
  showBorder?: boolean
  customSizeInPx?: number
}

const sizeMap = {
  'sm': '0.875rem', // 14px
  'md': '1rem',     // 16px
  'lg': '1.25rem',  // 20px
}

export function CountryFlag({ country, className = '', size = 'md', showBorder = true, customSizeInPx }: CountryFlagProps) {
  const [isLoaded, setIsLoaded] = useState(!!countriesCache)
  
  useEffect(() => {
    if (!countriesCache) {
      ensureCountriesLoaded().then(() => setIsLoaded(true))
    }
  }, [])
  
  const code = getCountryCode(country)
  const sizeValue = customSizeInPx ? `${customSizeInPx}px` : sizeMap[size]
  
  if (!isLoaded || !code) {
    return <span className={`${className} inline-flex items-center justify-center ${showBorder ? 'border-none' : ''}`}>🌍</span>
  }
  
  return (
    <span 
      className={`fi fi-${code} fis ${className} ${showBorder ? 'border border-border' : ''} rounded-full`}
      style={{ 
        width: sizeValue, 
        height: sizeValue, 
        lineHeight: sizeValue,
        minWidth: sizeValue,
        minHeight: sizeValue
      }}
    />
  )
}

// Preload countries on module import
if (typeof window !== 'undefined') {
  ensureCountriesLoaded()
}
